// Filter related helper functions
// - activateProposedFilters
// - addConfigFilters
// - alignPerspectiveOnLocationFilters
// - areEntityFiltersMisaligned
// - clearProposedEntityFilters
// - filtersReady
// - findFilterById
// - deactivateFilters
// - deactivateLocationFilters
// - deactivateSomeFilters
// - filterClick
// - formatFiltersForPost
// - isolateStateFilter;
// - locationFiltersOnly
// - noKeywords
// - onlyOnePerspective
// - openActiveFilters
// - parseActiveFilters;
// - parseEntityFilters
// - reactivateFilters;
// - reactivateFiltersByName;
// - refineFilters;
// - reformatContentFilteredByDate;
// - removeLocationFilters
// - restoreProposedStateForActiveEntityFilters
// - setLegendText
// - toggleEntityState

import _ from "underscore";

import { areArraysEqual, flattenNestedArray } from "@exovera/helpers";

export const DYNAMIC_FILTER_TYPE = "dynamic";
export const DYNAMIC_FILTER_RESULT_TYPE = "dynamic-result";
export const STATIC_FILTER_TYPE = "static";
export const MAX_DYNAMIC_FILTERS = 1000;
export const DYNAMIC_FILTERS_PAGE_SIZE = 50;
export const MEDIA_FILTER_PERSPECTIVE = "media";
export const DATA_EXPLORER_FILTER_PERSPECTIVE = "dataExplorer";

// Clones filters and sets proposed filters to active
export const activateProposedFilters = (proposedFilters) => {
	const activeFilters = [];
	_.each(proposedFilters, (filter) => {
		activeFilters.push({
			...filter,
			children: filter.children && filter.children.length > 0 && activateProposedFilters(filter.children),
			open: !!filter.open,
			active: filter.proposed,
		});
	});

	return activeFilters;
};

// Adds filter to an array of filters, if it's not on it already
// NOTE: this happens by name, not id (map filters have issues with id)
// filtersList can be an array of filters or false
// filter is a single filter or false
// Doesn't mutate parameters - all dependant functions duplicate their parameters
export const addConfigFilters = (filtersList, filter, baseFilters) => {
	const reactivatedFilters = filtersList ? reactivateFilters(baseFilters, filtersList) : baseFilters;
	// set filters by config. This potentially changes the location filters
	const configedFilters = filter ? reactivateFiltersByName(reactivatedFilters, [filter]) : reactivatedFilters;
	return parseActiveFilters(configedFilters);
};

// returns all location filters aligned with the passed perspective
//		Switches the filters perspective if necessary
// 		Expects to only receive location filters from one perspective
export const alignPerspectiveOnLocationFilters = (filters, perspective) => {
	const alignedFilters = [];
	let unadjustedFilters;
	if (perspective === "Source") {
		const audienceStates = _.filter(filters, (filter) => {
			return filter.parent === "audienceState";
		});
		_.each(audienceStates, (state) => {
			alignedFilters.push({
				active: state.active,
				id: state.id,
				name: state.name,
				parent: "sourceState",
			});
		});
		unadjustedFilters = _.reject(filters, (filter) => {
			return filter.parent === "audienceState";
		});
	} else if (perspective === "Audience") {
		const sourceStates = _.filter(filters, (filter) => {
			return filter.parent === "sourceState";
		});
		_.each(sourceStates, (state) => {
			alignedFilters.push({
				active: state.active,
				id: state.id,
				name: state.name,
				parent: "audienceState",
			});
		});
		unadjustedFilters = _.reject(filters, (filter) => {
			return filter.parent === "sourceState";
		});
	} else {
		console.error(`Incorrect perspective "${perspective}" passed to alignPerspectiveOnLocationFilters helper.`);
	}

	return [...unadjustedFilters, ...alignedFilters];
};

// returns true if any entity filter's active && proposed states don't match
export const areEntityFiltersMisaligned = (entities, optionsArr) => {
	// Flatten the entities, to searching them easier
	let flatEntities = [];
	_.each(optionsArr, (option) => {
		flatEntities = [...flatEntities, ...entities[option]];
	});
	const filtersMisaligned = _.find(flatEntities, (entity) => {
		return (!entity.active && entity.proposed) || (entity.active && !entity.proposed);
	});

	return !!filtersMisaligned;
};

// Returns a clone of entities set to proposed: false
export const clearProposedEntityFilters = (originalEntities, optionsArray) => {
	if (!optionsArray) {
		console.error("helper clearProposedEntityFilters requires optionsArray", optionsArray);
	}

	const entities = {};
	_.each(optionsArray, (option) => {
		entities[option] = [];
		_.each(originalEntities[option], (entity) => {
			entities[option].push({
				name: entity.name,
				value: entity.value,
				active: entity.active,
				proposed: false,
				type: entity.type,
			});
		});
	});

	return entities;
};

// Returns a clone of all Filters set to active: false
export const deactivateFilters = (array) => {
	const filters = array ? JSON.parse(JSON.stringify(array)) : [];
	_.each(filters, (filter) => {
		if (filter.children) {
			filter.children = deactivateFilters(filter.children);
		} else {
			filter.active = false;
			filter.proposed = false;
		}
	});
	return filters;
};

// Returns a clone of all fiters, with all locations set to false.
//    - will leave all of the filters open
export const deactivateLocationFilters = (originalFilters) => {
	const filters = JSON.parse(JSON.stringify(originalFilters));
	_.each(filters, (filter) => {
		if (filter.id === "audienceState" || filter.id === "sourceState") {
			filter.children = deactivateFilters(filter.children);
		}
	});
	return filters;
};

// Deactivates the filters in the toDeactivate array.
// matches filters by id, unless additionalKey is passed, then it will match by id and additionalKey
// Returns a clone of main filters

// main (required): array of object - usually all the filters (filters, graphFilters, etc)
// toDeactivate (required): array of objects - filters to deactivate
// additionalKey: string - only deactivates filters whose id and additionalKey match. Use with non-unique ids.
export const deactivateSomeFilters = (params) => {
	const { main, toDeactivate, additionalKey } = params;
	const filters = JSON.parse(JSON.stringify(main));

	_.each(toDeactivate, (remove) => {
		_.each(filters, (filter) => {
			if (additionalKey ? filter.id === remove.id && filter[additionalKey] === remove[additionalKey] : filter.id === remove.id) {
				filter.active = false;
				filter.proposed = false;
			}
			_.each(filter.children, (child) => {
				if (additionalKey ? child.id === remove.id && child[additionalKey] === remove[additionalKey] : child.id === remove.id) {
					child.active = false;
					child.proposed = false;
				}
			});
		});
	});

	return filters;
};

// Clone filters and toggle the proposed or open state, depending on whether the
//    element has children (only the final children are filters)
// This code is a bit heavy because we have to find the correct filter within
//    the JSON structure before we toggle it.
//    It works fine, but maybe it can be refactored.
export const filterClick = (originalFilters, filter) => {
	// console.log("filterClick helper", originalFilters, filter);

	const filters = JSON.parse(JSON.stringify(originalFilters));
	// if filter has children, toggle the open state
	if (filter.children) {
		// if filter is not top level
		// BUG: this no longer works
		// What is this actually doing?
		//    - Checks to see if the filter is mid level
		if (filter.parent !== "sourceInfo" && filter.parent !== "audienceInfo") {
			const parentFilter = _.find(filters, (parent) => {
				return _.find(parent.children, (child) => {
					return child.id === filter.id;
				});
			});
			const targetFilter = _.find(parentFilter.children, (x) => {
				return x.id === filter.id;
			});
			targetFilter.open = !filter.open;
		} else {
			// top level filter
			const targetFilter = _.find(filters, (x) => {
				return x.id === filter.id;
			});
			targetFilter.open = !filter.open;
		}
		// if there are no children, toggle the proposed state
	} else if (filter.grandparent) {
		// For grandchild elements
		const grandparentFilter = _.find(filters, (grandparent) => {
			return _.find(grandparent.children, (parent) => {
				return _.find(parent.children, (childFilter) => {
					return childFilter.id === filter.id;
				});
			});
		});
		const parentFilter = _.find(grandparentFilter.children, (parent) => {
			return _.find(parent.children, (child) => {
				return child.id === filter.id;
			});
		});
		const targetFilter = _.find(parentFilter.children, (x) => {
			return x.id === filter.id;
		});
		targetFilter.proposed = !filter.proposed;
	} else if (filter.parent) {
		// For child elements
		const parentFilter = _.find(filters, (parent) => {
			return _.find(parent.children, (child) => {
				return child.id === filter.id;
			});
		});
		const targetFilter = _.find(parentFilter.children, (x) => {
			return x.id === filter.id;
		});
		targetFilter.proposed = !filter.proposed;
	} else {
		// top level elements with no children (may not be a thing)
		const targetFilter = _.find(filters, (x) => {
			return x.id === filter.id;
		});
		targetFilter.proposed = !filter.proposed;
	}
	return filters;
};


export const simpleFilterClick = (originalFilters, filter) => {
	const filters = JSON.parse(JSON.stringify(originalFilters));
	if (filter && filter.id) {
		const matchedFilter = findFilterById(filters, filter.id);
		if (matchedFilter) {
			matchedFilter.proposed = !matchedFilter.proposed;
		}
		else {
			console.error("No match found for filter id: ", filter.id);
		}
	}	
	return filters;
};



// Returns true if there are filters ready to be filtered (should the "filter" button be active?)
// NOTE: proposedFilters & activeFilters, and visibleKeywords & activeKeywords are matched arrays, so they can be anything
//		example: { proposedFilters: state.proposedFilters, activeFilters: state.filters } is the same as:
//						{ proposedFilters: pluckActiveFilters(state.proposedFilters), activeFilters: state.filtersList }
export const filtersReady = (params) => {
	const {
		activeNarrativeData,
		proposedNarrative,
		proposedFilters,
		activeFilters,
		visibleKeywords,
		activeKeywords,
		entities,
		entityFiltersTypeOptions,
		relevancyMatches,
	} = params;

	// // ------ DEBUG ------
	// if (!activeNarrativeData) {
	// 	console.log("activeNarrativeData", activeNarrativeData);
	// } else if (activeNarrativeData && activeNarrativeData.id !== proposedNarrative.id) {
	// 	console.log("proposedNarrative.id", proposedNarrative.id);
	// } else if (!relevancyMatches) {
	// 	console.log("!relevancyMatches", !relevancyMatches);
	// } else if (!areArraysEqual(proposedFilters, activeFilters)) {
	// 	console.log(
	// 		"!areArraysEqual(proposedFilters, activeFilters)",
	// 		!areArraysEqual(proposedFilters, activeFilters)
	// 	);
	// 	console.log("proposedFilters, activeFilters", proposedFilters, activeFilters);
	// } else if (!areArraysEqual(visibleKeywords, activeKeywords)) {
	// 	console.log(
	// 		"!areArraysEqual(visibleKeywords, activeKeywords)",
	// 		!areArraysEqual(visibleKeywords, activeKeywords)
	// 	);
	// } else if (areEntityFiltersMisaligned(entities, entityFiltersTypeOptions)) {
	// 	console.log(
	// 		"areEntityFiltersMisaligned(entities, entityFiltersTypeOptions)",
	// 		areEntityFiltersMisaligned(entities, entityFiltersTypeOptions)
	// 	);
	// }

	return (
		!activeNarrativeData ||
		(activeNarrativeData && activeNarrativeData.id !== proposedNarrative.id) ||
		!relevancyMatches ||
		!areArraysEqual(proposedFilters, activeFilters) ||
		!areArraysEqual(visibleKeywords, activeKeywords) ||
		areEntityFiltersMisaligned(entities, entityFiltersTypeOptions)
	);
};

// accepts an array of active filters.
// returns an array of objects, one for each parent filter:
// filters: [
//   { parent1: [221, 331, 441, 551] },
//   { parent2: ['apples', 'oranges', 'taxes'] }
// ]
export const formatHierarchyFiltersForPost = (filtersList) => {
	const getChildren = (filters) => {
		let kids = [];
		for (const filter of filters) {
			if (Array.isArray(filter.children) && filter.children.length > 0) {
				kids = kids.concat(getChildren(filter.children));
			}
			else if (filter.parent && filter.active) {
				kids.push(filter);
			}
		}
		return kids;
	}
	const ret = formatFiltersForPost(getChildren(filtersList));
	return ret;
};

// accepts an array of active filters.
// returns an array of objects, one for each parent filter:
// filters: [
//   { parent1: [221, 331, 441, 551] },
//   { parent2: ['apples', 'oranges', 'taxes'] }
// ]
export const formatFiltersForPost = (filtersList) => {
	// console.log("formatFiltersForPost", filtersList);
	// Make a unique list of top level parent filters
	const filterParents = _.uniq(
		_.map(filtersList, (filter) => {
			return filter.grandparent || filter.parent;
		})
	);
	// assign filters to each parent
	const filters = [];
	_.each(filterParents, (parent) => {
		const childFilters = _.filter(filtersList, (filter) => {
			return filter.grandparent === parent || filter.parent === parent;
		});
	
		filters.push({
			[parent]: _.map(childFilters, (child) => {
				return child.id || child.name;
			}),
		});
		// TODO: Reinstall this once the API requires filtering the entities by type
		// // Entities have to be further sorted by type
		// if (parent === "entities") {
		// 	const entityFilters = _.filter(filtersList, (filter) => {
		// 		return filter.parent === parent;
		// 	});
		// 	const typeFilters = _.groupBy(entityFilters, (filter) => {
		// 		return filter.type;
		// 	});
		// 	const stringEntityFilters = _.map(typeFilters, (filter, key) => {
		// 		return { [key]: _.pluck(filter, "id") };
		// 	});
		// 	filters.push({ entities: stringEntityFilters });
		// } else {
		// 	const childFilters = _.filter(filtersList, (filter) => {
		// 		return filter.parent === parent;
		// 	});
		// 	filters.push({ [parent]: _.pluck(childFilters, "id") });
		// }
	});

	return filters;
};

// accepts an array of active filters.
// returns an array of string, one for each child filter:
// ["a.b", "c.d.e", ...],
export const formatQueryAddsForPost = (filtersList) => {
	const queryAdds = [];
	_.each(filtersList, (filter) => {
		if (filter.queryAdd) {
			queryAdds.push(filter.queryAdd)
		}
	})
	return queryAdds;
};

// Deactivate location filters, except for the current state
export const isolateStateFilter = (filters, stateData) => {
	const stateFilters = filters.map((filt) => {
		// only change location filters
		if (filt.id === "sourceState" || filt.id === "audienceState") {
			const locationFilter = deactivateFilters([filt])[0]; // returns first item since we're just passing one filter type array (location)

			const stateIndex = _.findIndex(locationFilter.children, (state) => {
				// return state.name === stateData.name;
				// TODO: replace with line above when typos are fixed.
				const aState = state.name === "Lousiana" ? "Louisiana" : state.name === "Kansa" ? "Kansas" : state.name;
				return aState === stateData.name;
			});

			if (stateIndex >= 0) {
				locationFilter.children[stateIndex].active = true;
			}
			return locationFilter;
		} else {
			return filt;
		}
	});

	return parseActiveFilters(stateFilters);
};

// Returns a cloned array of location filters
//		Expects flat array of active filters
export const locationFiltersOnly = (filtersListOriginal) => {
	const filtersList = JSON.parse(JSON.stringify(filtersListOriginal));
	const filtersWithLocation = _.filter(filtersList, (filter) => {
		return filter.parent === "audienceState" 
			|| filter.parent === "sourceState"
			|| filter.parent === "audienceProvince"
			|| filter.parent === "sourceProvince";
	});
	return !filtersListOriginal ? [] : filtersWithLocation;
};

// Adds the field 'filterType' with the parent's "name" to each filter,
// Also adds the field 'label' with the supplied value
export const markFilters = (params) => {
	const { filters, value } = params;

	_.each(filters, (filter) => {
		filter.label = value;
		_.each(filter.children, (child) => {
			child.filterType = filter.name;
			child.label = value;
		});
	});

	return filters;
};

export const noKeywords = (filters) => {
	return _.filter(filters, (filter) => {
		return filter.parent !== "keywords";
	});
};

// filter out filters without the supplied perspective
// NOTE: expects filters to have had a perspective flag added "Source", "Audience", "nonNarrative"
export const onlyOnePerspective = (params) => {
	const { filters, perspective } = params;
	console.log("onlyOnePerspective", filters, perspective);
	return _.filter(filters, (filter) => {
		return filter.filterType === perspective;
	});
};

// open any filters with active children
export const openActiveFilters = (filters) => {
	_.each(filters, (parentFilter) => {
		const activeChild = _.find(parentFilter.children, (filter) => {
			return filter.active;
		});
		if (activeChild) {
			parentFilter.open = true;
		}
	});
	return filters;
};

// Returns an array of all proposed Filters or false if there are none
//   Calls itself recursively
//   No mutation
//	NOTE: Returns false instead of an empty array
// Assumes all active filters are proposed
export const parseActiveFilters = (filters) => {
	let activeFilters = [];
	_.each(filters, (filter) => {
		if (filter.children) {
			const activeKids = parseActiveFilters(filter.children);
			if (activeKids) {
				activeFilters = [...activeFilters, ...activeKids];
			}
		} else if (filter.proposed) {
			activeFilters.push({
				...filter,
			});
		}
	});
	return activeFilters.length > 0 ? activeFilters : false;
};

// Returns a one to many parent -> child map of active filters e.g.
// { parentid: {childId: <child>, childId: <child>, ...}, parentId: {...}
export const createParentFilterMap = (filters) => {
	const filterMap = {};
	_.each(filters, (filter) => {
		if (!filterMap[filter.parentId]) {
			filterMap[filter.parentId] = {};
		}
		filterMap[filter.parentId][filter.id] = filter;
	});
	return filterMap;
}

// Returns an array of all active Filters (objects) or false if there are none
//   No mutation
// useProposed: true will only return proposed entities
//	NOTE: Returns false instead of an empty array
export const parseEntityFilters = (filters, typesArray, useProposed) => {
	if (!typesArray) {
		console.error("helper parseEntityFilters requires typesArray", typesArray);
	}

	const activeFilters = [];

	_.each(typesArray, (type) => {
		_.each(filters[type], (entity) => {
			if (
				(!useProposed && entity.active) ||
				// (entity.parent === "entities" && (!useProposed && entity.active)) ||
				(useProposed && entity.proposed)
			) {
				activeFilters.push({
					active: entity.active,
					name: entity.name,
					proposed: entity.proposed,
					type: entity.type,
					value: entity.value,
				});
			}
		});
	});

	return activeFilters.length > 0 ? activeFilters : false;
};

export const toggleEntities = (entitiesOG, entityTypeOptions, toggleThis) => {
	const entities = [];
	_.each(entityTypeOptions, (type) => {
		entities[type] = [];
		_.each(entitiesOG[type], (entity) => {
			entities[type].push({
				name: entity.name,
				type: entity.type,
				value: entity.value,
				active: entity.name === toggleThis ? !entity.active : entity.active,
				proposed: entity.proposed,
			});
		});
	});

	return entities;
};

// Returns a clone of all Filters, with active filters matching those on the supplied list
//    Works without parentFilters, so it can match filters from different perspectives
// baseFilterArray: Array of filter objects
// activeFilterArray: Array of filter objects or false (just returns baseFilterArray)
export const reactivateFilters = (baseFilterArray, activeFilterArray, cloneFilters=true) => {
	const filters = !baseFilterArray ? [] : cloneFilters ? JSON.parse(JSON.stringify(baseFilterArray)) : baseFilterArray;
	let activeFilters = !activeFilterArray ? [] : cloneFilters ? JSON.parse(JSON.stringify(activeFilterArray)) : activeFilterArray;

	if (activeFilterArray && activeFilterArray.length > 0) {
		_.each(filters, (parentFilter) => {
			_.find(parentFilter.children, (filter) => {
				const shouldBeActive = _.find(activeFilters, (activeFilter) => {
					return activeFilter && filter && activeFilter.id === filter.id;
				});
				if (shouldBeActive) {
					// remove activeFilters to shorten cycles
					activeFilters = _.without(activeFilters, (activeFilter) => {
						return activeFilter.id === shouldBeActive.id;
					});
					// make it active & proposed
					filter.active = true;
					filter.proposed = true;
				}
				// Bail if we're out of activeFilters
				if (activeFilters.length <= 0) {
					return false;
				}
			});
			if (parentFilter.children) {
				reactivateFilters(parentFilter.children, activeFilters, false);
			}
		});

		// handle any filters that don't exist in the current set (e.g. for dynamic filters)
		_.each(activeFilters, (activeFilter) => {
			if (activeFilter.parentId && activeFilter.type === DYNAMIC_FILTER_RESULT_TYPE) {
				const parentFilter = findFilterById(filters, activeFilter.parentId);
				if (parentFilter) {
					const matchingChild = _.find(parentFilter.children, (filter) => {
						return filter.id === activeFilter.id;
					});
					if (!matchingChild) {
						if (!parentFilter.children) {
							parentFilter.children = [];
						}
						parentFilter.children.push(activeFilter)
					}
				}
			}
		})
	}
	return openActiveFilters(filters);
};

// Similar to the above function, but does it by name instead of id
//    NOTE: Only works on children - no grandchildren
export const reactivateFiltersByName = (array, activeFilterArray) => {
	const filters = array ? JSON.parse(JSON.stringify(array)) : [];
	let activeFilters = activeFilterArray ? JSON.parse(JSON.stringify(activeFilterArray)) : [];

	if (activeFilterArray && activeFilterArray.length > 0) {
		_.each(filters, (parentFilter) => {
			_.find(parentFilter.children, (filter) => {
				const shouldBeActive = _.find(activeFilters, (activeFilter) => {
					return activeFilter.name === filter.name;
				});
				if (shouldBeActive) {
					// remove activeFilters to shorten cycles - helps because activeFilters should be way shorter than filters
					activeFilters = _.without(activeFilters, (activeFilter) => {
						return activeFilter.name === shouldBeActive.name;
					});
					// make it active
					filter.active = true;
					filter.proposed = true;
				}
				// Bail if we're out of activeFilters
				if (activeFilters.length <= 0) {
					return false;
				}
			});
		});
	}

	return openActiveFilters(filters);
};

// Similar to the above function, but does it by name instead of id
export const reactivateObjectBasedFiltersByName = (array, activeFilterArray) => {
	const filters = array ? JSON.parse(JSON.stringify(array)) : [];
	let activeFilters = activeFilterArray ? JSON.parse(JSON.stringify(activeFilterArray)) : [];

	if (activeFilterArray && activeFilterArray.length > 0) {
		_.each(filters, (filterGroup) => {
			_.find(filterGroup, (filter) => {
				const shouldBeActive = _.find(activeFilters, (activeFilter) => {
					return activeFilter.name === filter.name;
				});
				if (shouldBeActive) {
					// remove activeFilters to shorten cycles - helps because activeFilters should be way shorter than filters
					activeFilters = _.without(activeFilters, (activeFilter) => {
						return activeFilter.name === shouldBeActive.name;
					});
					// make it active
					filter.active = true;
					filter.proposed = true;
				}
				// Bail if we're out of activeFilters
				if (activeFilters.length <= 0) {
					return false;
				}
			});
		});
	}

	return openActiveFilters(filters);
};

// Returns a clone of all originalFilters that also exist in availableFilters
//    originalFilters: flat array objects. ie, works with state.activeFilters, but not state.filters.
export const refineFilters = (originalFilters, availableFilters) => {
	const filters = [];
	// flatten availableFilters
	const flatFilters = flattenNestedArray(availableFilters);

	_.each(originalFilters, (filter) => {
		const filterExists = _.find(flatFilters, (flat) => {
			return filter.id === flat.id;
		});
		if (filterExists) {
			filters.push({
				name: filterExists.name,
				id: filterExists.id,
				parent: filterExists.parent,
			});
		}
	});
	return filters;
};

// Updates contentFilteredByDate object to fit the supplied width
// 		left is way off when moving to the compare mode
//	NOTE: Relies on an accurate crosshairWidth
export const reformatContentFilteredByDate = (params) => {
	const { contentFilteredByDate, trimmedSeries, graphWidth, aggregationLevel } = params;
	if (!contentFilteredByDate || !trimmedSeries || graphWidth === undefined) {
		return false;
	} else {
		const crosshairWidth = graphWidth / (trimmedSeries.length - 1) > 1 ? graphWidth / (trimmedSeries.length - 1) : 1;
		const isAggregated = aggregationLevel && aggregationLevel !== "timeseries";
		let dateToCheck = contentFilteredByDate.begin;
		if (isAggregated) {
			dateToCheck = contentFilteredByDate.end - (contentFilteredByDate.end % (24*60*60*1000));
		}			
		const dateIndex = _.findIndex(trimmedSeries, (date) => {
			return Number(date) === Number(dateToCheck);
		});

		const reformattedContentFilteredByDate = {
			begin: contentFilteredByDate.begin,
			end: contentFilteredByDate.end,
			indicatorOffset: dateIndex >= 0 ? crosshairWidth * dateIndex : contentFilteredByDate.indicatorOffset,
			indicatorWidth: contentFilteredByDate.indicatorWidth,
		};

		return reformattedContentFilteredByDate;
	}
};

// Removes location filters.
// Expects a list of active filters only.
//	returns an array of cloned filters
export const removeLocationFilters = (filtersListOriginal) => {
	const filtersList = JSON.parse(JSON.stringify(filtersListOriginal));
	const filterWithoutLocation = _.reject(filtersList, (filter) => {
		return filter.parent === "audienceState" || filter.parent === "sourceState" ||
		filter.parent === "audienceProvince" || filter.parent === "sourceProvince";
	});
	return filterWithoutLocation;
};

// Removes source country filters.
// Expects a list of active filters only.
//	returns an array of cloned filters
export const removeSourceCountryFilters = (filtersListOriginal) => {
	if (!Array.isArray(filtersListOriginal)) {
		return filtersListOriginal;
	}
	const filtersList = JSON.parse(JSON.stringify(filtersListOriginal));
	const filterWithoutCountry = _.reject(filtersList, (filter) => {
		return filter.parent === "sourceCountry";
	});
	return filterWithoutCountry;
};

// Removes state filters. If stateIdPrefix is specified, only removes
// state filters whose id does not begin with the specified string
// Expects a list of active filters only.
//	returns an array of cloned filters
export const removeStateFilters = (filtersListOriginal, stateIdPrefix) => {
	if (!Array.isArray(filtersListOriginal)) {
		return filtersListOriginal;
	}
	const filtersList = JSON.parse(JSON.stringify(filtersListOriginal));
	const filterWithoutStates = _.reject(filtersList, (filter) => {
		const isStateFilter = filter.parent === "audienceState" 
			|| filter.parent === "sourceState" 
			|| filter.parent === "audienceProvince"
			|| filter.parent === "sourceProvince";
		return isStateFilter && (!stateIdPrefix || !filter.id.startsWith(stateIdPrefix));
	});
	return filterWithoutStates;
};

// Checks if any country filters are currently active
// Expects a list of active filters only.
//	returns true or false
export const hasCountryFilters = (filtersList) => {
	if (!Array.isArray(filtersList)) {
		return false;
	}
	return filtersList.some(filter => {
		if (filter.parent === "sourceCountry") {
			return true;
		}
		return false;
	});
};

// Returns a list of country filters present the provided filter 
// list or an empty list if no country filters are found
export const getCountryFilters = (filtersList) => {
	if (!Array.isArray(filtersList)) {
		return [];
	}
	return filtersList.filter(f => {
		if (f.parent === "sourceCountry") {
			return true;
		}
		return false;
	});
};

// Returns a clone of entities with all entities set to active: true also set to proposed: true,
//		and all entities set to proposed: true and active: false set to proposed: false
export const restoreProposedStateForActiveEntityFilters = (originalEntities, optionsArray) => {
	if (!optionsArray) {
		console.error("helper restoreProposedStateForActiveEntityFilters requires optionsArray", optionsArray);
	}
	const entities = JSON.parse(JSON.stringify(originalEntities));

	_.each(optionsArray, (option) => {
		_.each(entities[option], (entity) => {
			if (entity.active) {
				entity.proposed = true;
			} else if (entity.proposed && !entity.active) {
				entity.proposed = false;
			}
		});
	});

	return entities;
};

export const selectedFilterText = (filterArr) => {
	const names = _.pluck(parseActiveFilters(filterArr), "name");
	if (names.length <= 0) {
		return "None selected";
	} else if (names.length === 1) {
		return names[0];
	} else if (names.length === 2) {
		return `${names[0]} + ${names[1]}`;
	} else {
		return `${names[0]} + ${names.length - 1} more`;
	}
};

export const setLegendText = (params) => {
	const { lines, filters } = params;
	// console.log("setLegendText", lines, filters);

	return lines;
};

// Sets a passed field in an object within an array to whatever the passed value is
// 	If no key is passed, it will look for "active"
//	value: Any - usually a boolean

// If the object isn't found, it returns the original array
// NOTE: mutates the original array, so make sure to clone it first
export const setNestedFieldById = (params) => {
	const { array, childName, key, id, value } = params;
	const active = key || "active";
	if (!array) {
		return false;
	}

	const objIndex = _.findIndex(array, (obj) => {
		return obj.id === id;
	});

	// If the object is in the top level
	if (objIndex >= 0) {
		array[objIndex][active] = value;
		return array;
	} else {
		_.each(array, (obj) => {
			if (obj[childName]) {
				const childArray = setNestedFieldById({ array: obj[childName], key, childName, id, value });
				obj[childName] = childArray;
			}
		});
		return array;
	}
};

// Toggles the proposed state for the passed entity
// Does not mutate entities
export const toggleEntityState = (entities, entity, entityType) => {
	const entitiesObj = JSON.parse(JSON.stringify(entities));
	const currentEntityList = entitiesObj[entityType];
	_.find(currentEntityList, (item) => {
		if (item.name === entity.name) {
			// If it's the first click of an active state, it should behave as if it was already proposed
			const originalProposedState = item.proposed === undefined && item.active ? true : item.proposed;
			item.proposed = !originalProposedState;
		}
	});

	return entitiesObj;
};

export const convertMapCodeToEntityFilter = (params) => {
	const { stateObject, mapLookup, parent, active } = params;
	if (!stateObject || _.isEmpty(stateObject)) {
		return {};
	} else {
		const { name, geoState, shares } = stateObject;
		const usableId = mapLookup ? mapLookup[geoState] : geoState;
		const entityFilter = usableId
			? {
					name,
					label: name, // for BarChart
					id: usableId,
					value: shares,
					parent: parent || "sourceState",
					type: "map",
					active: active !== undefined ? active : undefined,
					proposed: active, 
			  }
			: {};
		return entityFilter;
	}
};

export const genCountryFilter = (params) => {
	const { countryId, active } = params;
	const newActive = active !== undefined ? active : undefined
	return {
		name: countryId,
		label: undefined,
		id: `sourceCountry_${countryId.charAt(0).toUpperCase() + countryId.slice(1)}`,
		value: undefined,
		parent: "sourceCountry",
		type: undefined,
		active: newActive,
		proposed: newActive, 
	}
};

export const findFilterById = (filters, id) => {
	for(let i=0; i<filters.length; i++) {
		const filterRoot = filters[i];
		if (filterRoot.id === id) {
			return filterRoot;
		} else if (Array.isArray(filterRoot.children) && filterRoot.children.length > 0) {
			const childMatch = findFilterById(filterRoot.children, id);
			if (childMatch) {
				return childMatch;
			}
		}
	}
}
export const getFiltersByType = (filters, type, outFilters=[]) => {
	for(let i=0; i<filters.length; i++) {
		const filterRoot = filters[i];
		if (filterRoot.type === type) {
			outFilters.push(filterRoot);
		} 
		if (Array.isArray(filterRoot.children) && filterRoot.children.length > 0) {
			findFilterById(filterRoot.children, type, outFilters);
		}
	}
	return outFilters;
}

export const getActiveChildren = (filter) => {
	let activeKids = [];
	if (Array.isArray(filter.children)) {
		for(let i=0; i<filter.children.length; i++) {
			const nextChild = filter.children[i];
			if (nextChild.active) {
				activeKids.push(nextChild);
			} 
			else if (Array.isArray(nextChild.children) && nextChild.children.length > 0) {
				activeKids = activeKids.concat(getActiveChildren(nextChild));
			}
		}
	}
	return activeKids;
}

export const decorateFilters = (filters) => {
	if (Array.isArray(filters)) {
		filters.forEach(parent => {
			if (parent.children instanceof Array) {
				parent.type = parent.type || STATIC_FILTER_TYPE;
				parent.pageNum = 1;
				parent.filterFilterString = "";
				parent.pendingFilterFilterString = "";
				parent.status = "closed";
				parent.children.forEach(child => {
					child.parentDisplayName = parent.name;
					child.parentId = parent.id;
					decorateFilters([child]);
				})
			}
		})
	}
}